import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, FileText, Calendar, User, Search, ArrowLeft } from 'lucide-react';
import { format } from 'date-fns';
import './FoliosList.css';
import UserAvatar from './UserAvatar';

function FoliosList() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [folios, setFolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchFolios();
  }, [projectId]);

  const fetchFolios = async () => {
    try {
      const response = await axios.get(`/folios/project/${projectId}`);
      setFolios(response.data);
    } catch (error) {
      console.error('Failed to fetch folios:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFolio = async () => {
    console.log('Creating new folio for project:', projectId);
    try {
      const response = await axios.post('/folios', {
        project: projectId,
        title: 'Untitled Folio',
        content: ''
      });
      console.log('Folio created:', response.data);
      const newPath = `/project/${projectId}/folio/${response.data._id}`;
      console.log('Navigating to:', newPath);
      navigate(newPath);
    } catch (error) {
      console.error('Failed to create folio:', error);
      alert('Failed to create folio: ' + (error.response?.data?.error || error.message));
    }
  };

  const filteredFolios = folios.filter(folio =>
    folio.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="folios-list-page">
      <div className="folios-list-header">
        <div className="folios-list-header-top">
          <button 
            className="btn btn-ghost" 
            onClick={() => navigate(`/project/${projectId}`)}
          >
            <ArrowLeft size={18} />
            Back to Project
          </button>
        </div>
        <div className="folios-list-title-row">
          <div className="folios-list-title">
            <FileText size={24} />
            <h1>Folios</h1>
            <span className="folios-count">{folios.length}</span>
          </div>
          <button className="btn btn-primary" onClick={handleCreateFolio}>
            <Plus size={18} />
            New Folio
          </button>
        </div>
      </div>

      <div className="folios-list-actions">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search folios..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="folios-loading">
          <div className="loading-spinner"></div>
          <p>Loading folios...</p>
        </div>
      ) : filteredFolios.length === 0 ? (
        <div className="folios-empty">
          <FileText size={64} style={{ opacity: 0.2 }} />
          {searchQuery ? (
            <>
              <h3>No folios found</h3>
              <p>Try a different search term</p>
            </>
          ) : (
            <>
              <h3>No folios yet</h3>
              <p>Create your first documentation page to get started</p>
              <button className="btn btn-primary" onClick={handleCreateFolio}>
                <Plus size={18} />
                Create First Folio
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="folios-grid">
          {filteredFolios.map(folio => (
            <div
              key={folio._id}
              className="folio-card"
              onClick={() => navigate(`/project/${projectId}/folio/${folio._id}`)}
            >
              <div className="folio-card-icon">
                <FileText size={32} />
              </div>
              <div className="folio-card-content">
                <h3>{folio.title}</h3>
                <p className="folio-card-preview">
                  {folio.content 
                    ? folio.content.substring(0, 120).replace(/[#*`_~]/g, '') + (folio.content.length > 120 ? '...' : '')
                    : 'No content yet'}
                </p>
              </div>
              <div className="folio-card-footer">
                <div className="folio-card-author">
                  {/* WR-018 §2: shared component — see FolioEditor. */}
                  <UserAvatar user={folio.author} className="avatar avatar-xs" />
                  <span>{folio.author?.name}</span>
                </div>
                <div className="folio-card-date">
                  <Calendar size={14} />
                  <span>{format(new Date(folio.updatedAt), 'MMM d, yyyy')}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default FoliosList;
